export const TOKEN_KEY = 'rp_token';
export const USER_KEY = 'rp_user';
export const REFRESH_KEY = 'rp_refresh_token';
export const REMEMBER_UNTIL_KEY = 'rp_remember_until';

/* "Remember this device" on the code page keeps the session alive this long
   after the code is entered, counted from sign-in (not from last use). */
export const REMEMBER_DEVICE_DAYS = 30;
const REMEMBER_DEVICE_MS = REMEMBER_DEVICE_DAYS * 24 * 60 * 60 * 1000;

/* Auth providers name the session token all sorts of ways and nest it at
   different depths — search the verify response for the first plausible one. */
const TOKEN_FIELD_NAMES = [
  'accessToken', 'access_token', 'AccessToken',
  'idToken', 'id_token', 'IdToken',
  'authToken', 'auth_token', 'sessionToken', 'jwt', 'token',
];

export function findToken(obj, depth = 0) {
  if (!obj || typeof obj !== 'object' || depth > 4) return null;
  for (const key of TOKEN_FIELD_NAMES) {
    if (typeof obj[key] === 'string' && obj[key].length > 8) return obj[key];
  }
  for (const value of Object.values(obj)) {
    if (value && typeof value === 'object') {
      const found = findToken(value, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
}

function tokenClaims(token) {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

/* Reads the exp claim (ms) from a JWT; null for opaque/non-JWT tokens. */
function tokenExpiry(token) {
  const payload = tokenClaims(token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
}

/* The logged-in user's auth0 id ("auth0|…") from the stored JWT's sub claim. */
export function getTokenSubject() {
  const token = getStoredToken();
  if (!token) return null;
  const payload = tokenClaims(token);
  return typeof payload?.sub === 'string' ? payload.sub : null;
}

/* A JWT is valid until its exp passes. An opaque token can't be checked
   client-side, so it's trusted until the API answers 401. */
export function isTokenValid(token) {
  if (!token) return false;
  const exp = tokenExpiry(token);
  return exp === null || exp > Date.now();
}

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/* A remembered device keeps the refresh token from sign-in, so the API client
   can swap the 24-hour access token for a fresh one until the window closes. */
export function rememberDevice(refreshToken) {
  localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(REMEMBER_UNTIL_KEY, String(Date.now() + REMEMBER_DEVICE_MS));
}

export function forgetDevice() {
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(REMEMBER_UNTIL_KEY);
}

/* The stored refresh token, only while the device's 30 days are still running. */
export function getRememberedRefreshToken() {
  const until = Number(localStorage.getItem(REMEMBER_UNTIL_KEY));
  return until > Date.now() ? localStorage.getItem(REFRESH_KEY) : null;
}

export function clearStoredSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  forgetDevice();
}
